import { useEffect } from 'react';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import * as customBlocksState from '@/components/Analysis/modules/CustomBlocks/state';
import styled from '@emotion/styled';
import { Inspection3dViewer } from './Inspection3dViewer';
import { InspectionDataLoader } from './InspectionDataLoader';
import { selectedStructureId } from '@/state';
import { CustomBlockCuboidColorLegends } from '../modules/CustomBlocks/components/CustomBlockCuboidColorLegends';
import { useQueryParametersFromState, useSetStateFromQueryParameters } from '../deeplink';
import { ScreenshotViewer } from './Inspection3dViewer/ScreenshotViewer';
import { Snackbar } from '@/components/shared/Snackbar';
import { Box } from '@mui/material';
import { PageLoader } from '@/components/shared/PageLoader';

const Container = styled.div`
  overflow-x: hidden;
  flex-grow: 1;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

export const Viewer = ({ inspectionId }: { inspectionId: string }) => {
  const [snackbarMessage, setSnackbarMessage] = useAtom(state.snackbarMessage);
  const setSelectedStructureId = useSetAtom(selectedStructureId);

  useEffect(() => {
    setSelectedStructureId(inspectionId);
  }, [setSelectedStructureId, inspectionId]);

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };

  const { shouldShow, content, persistent, duration } = snackbarMessage;

  const lastDeeplinkUrlParameters = useAtomValue(state.lastDeeplinkUrlParameters);
  const screenshotMode = useAtomValue(state.screenshotMode);
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);
  const selectedCustomBlock = useAtomValue(customBlocksState.selectedCustomBlock);
  const selectedSavedBlockId = useAtomValue(customBlocksState.selectedSavedBlockId);
  const setAreSphericalsVisible = useSetAtom(state.areSphericalsVisible);
  const setArePoisVisible = useSetAtom(state.arePoisVisible);

  useEffect(() => {
    if (!screenshotMode) {
      const hasSelection = !!selectedAssemblyId || !!selectedCustomBlock || !!selectedSavedBlockId;
      setAreSphericalsVisible(!!selectedAssemblyId);
      if (hasSelection) {
        setArePoisVisible(true);
      }
    }
  }, [
    screenshotMode,
    selectedAssemblyId,
    selectedCustomBlock,
    selectedSavedBlockId,
    setArePoisVisible,
    setAreSphericalsVisible,
  ]);

  useSetStateFromQueryParameters();
  useQueryParametersFromState();

  if (!inspectionId || Array.isArray(inspectionId)) {
    return <div>Please specify inspection id, asset id and asset tag</div>;
  }

  const deeplinkIsReady = !!lastDeeplinkUrlParameters;

  return (
    <Wrapper>
      <Container>
        <InspectionDataLoader />
        {!deeplinkIsReady ? (
          <Box sx={{ paddingTop: '30rem' }}>
            <PageLoader />
          </Box>
        ) : screenshotMode ? (
          <ScreenshotViewer />
        ) : (
          <>
            <Snackbar
              open={shouldShow}
              onClose={closeSnack}
              persistent={persistent}
              autoHideDuration={duration}
            >
              {content}
            </Snackbar>
            <CustomBlockCuboidColorLegends />
            <Inspection3dViewer />
          </>
        )}
      </Container>
    </Wrapper >
  );
};
